import React, { useEffect, useState } from 'react';
import Drawer from 'Douleutaras/components/Drawer/Drawer';
import DrawerPanel from 'Douleutaras/components/Drawer/DrawerPanel';
import { isMobile } from 'react-device-detect';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import promotionBannerSlice from 'Douleutaras/redux/data/ui/promotionBannerSlice';
import { useMatch } from 'react-router';
import { URLS } from 'Douleutaras/constants/urls';
import RegularButton from 'Douleutaras/components/RegularButton/RegularButton';
import { Trans } from 'react-i18next';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { isMobileWebviewUserAgent } from 'Douleutaras/app/lib/mobileUtilities';
import { getAbsoluteUrl } from 'Douleutaras/utilities/url-builder';

import './MobilePromotionBanner.scss';

export const MobilePromotionBanner = () => {
  const { show, showInChat } = useAppSelector(state => state.promotionBanner);
  const { countryCode } = useLocalization();

  // Add paths / URLS that are excluded from showing the promo banner
  const excludedPaths = URLS.PRO_PROFILE.INDEX;

  // Exclude /pro/ routes
  const isProRoute = Boolean(useMatch({
    path: URLS.PRO_DASHBOARD.INDEX,
    end: false,
    caseSensitive: false
  }));

  // Exclude chat routes
  const isChatRoute = Boolean(useMatch({
    path: getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.INDEX'),
    end: true,
    caseSensitive: false
  }));

  // Exclude Pros List job posting flow routes
  const isProListRoute = Boolean(useMatch({
    path: getAbsoluteUrl('CLIENT_DASHBOARD.PRO_LIST'),
    end: true,
    caseSensitive: false
  }));

  // Exclude the post-job loading / matching carousel screen
  const isJobDoneRoute = Boolean(useMatch({
    path: URLS.JOB_DONE,
    end: false,
    caseSensitive: false
  }));

  // Exclude the job page (posting flow landing). Matched by route rather than the
  // transient `newly_created` param, so the banner also stays hidden on refresh,
  // back navigation and re-entry from email / notifications.
  const isJobPageRoute = Boolean(useMatch({
    path: getAbsoluteUrl('CLIENT_DASHBOARD.JOB.INDEX'),
    end: false,
    caseSensitive: false
  }));

  // Exclude from other defined routes
  const isOtherExcludedRoute = Boolean(useMatch({
    path: excludedPaths,
    end: true,
    caseSensitive: false
  }));

  // Do not show a banner for mobile webview
  const isMobileWebview = isMobileWebviewUserAgent();

  const actuallyShowBanner = isChatRoute ? showInChat : show;

  if (!isMobile) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }
  const { closePromotionBanner } = promotionBannerSlice.actions;
  const dispatch = useAppDispatch();

  const onPromoBannerClose = () => {
    dispatch(closePromotionBanner({ isChatRoute }));
  };

  // A boolean flag which triggers on time delay, to show components that need a small delay on load
  const [ showDelayedComponent, setShowDelayedComponent ] = useState(false);

  useEffect(() => {
    actuallyShowBanner && setTimeout(() => setShowDelayedComponent(true), 3000);
  }, []);

  if (
    !showDelayedComponent ||
    !actuallyShowBanner ||
    isProRoute ||
    isProListRoute ||
    isJobDoneRoute ||
    isJobPageRoute ||
    isMobileWebview ||
    isOtherExcludedRoute
  ) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  return <Drawer>
    <DrawerPanel>
      <article styleName="promo-banner">
        <header styleName="promo-banner__header">
          <h6 styleName="promo-banner__title">
            {<Trans>T.MOBILE-BANNER.PROMO TITLE</Trans>}
          </h6>
        </header>
        <section styleName="promo-banner__content">
          <p styleName="promo-banner__disclaimer">
            {<Trans>T.MOBILE-BANNER.PROMO BODY</Trans>}
          </p>
        </section>
        <section styleName="promo-banner__actions">
          <div styleName="promo-banner__actions-item">
            <RegularButton modifier="primary" title={<Trans>T.MOBILE-BANNER.PRIMARY CTA</Trans>} fullWidth size="large"
                           isAnchor
                           href={COUNTRY_CONFIG.MOBILE_APP_LINK[countryCode]}
            />
          </div>
          <div styleName="promo-banner__actions-item">
            <button styleName="promo-banner__close" onClick={onPromoBannerClose} type="button">
              <span styleName="promo-banner__close-title">
                {<Trans>T.MOBILE-BANNER.CLOSE</Trans>}
              </span>
            </button>
          </div>
        </section>
      </article>
    </DrawerPanel>
  </Drawer>;
};

export default MobilePromotionBanner;
